import MapboxStyleManager from './MapboxStyleManager'
import StoryDataManager from './StoryDataManager'
import StatsManager from './StatsManager'

// master list of all possible layers
import listOfAllPossibleLayers from '../content/list-of-all-possible-layers-and-their-info/list-of-all-possible-layers-and-their-info'

import { getMapboxStyleWithFreshCartoUrl } from './utilsCartoApi'
import { listToLookupTable } from './utilsData'
import {NECESSARY_FIELDS_PLACEHOLDER} from "../constants";
import {FIVE_CHLOROPLETH_COLORS} from "./utilsColorTheme";
import {getLayerSpecificFields, isAttrLayer} from "../utilsData";

import sampleStoryMap from './../content/storymaps/oz-dashboard-storymap'

// =====================================================================
// class DataManager
// Singleton responsible for managing all other data managers, and thus ALL data loaded in from ANY source (json, http requests, etc) -- works to abstract away management of the data from the logic that only reads it
// =====================================================================
class DataManager {
	constructor() {
		this._isReady = false

		// init constants
		this.CARTO_DATA = {}
		this.LOCAL_ASSET_CARTO_DATA = {}
		this.LAYER_NAMES_GROUPED_BY_CATEGORY = {}
		this.LAYER_NAMES_TO_ID = {}
		this.LAYER_NAMES = []
		this.DISPLAY = {}
		this.LAYER_LABEL_FIELD_IF_ANY = {}
		this.POLYGON_DATA_FILL_COLOR_IF_ANY = {}
		this.POINT_DATA_ICON_IF_ANY = {}
		this.FIELDS_TO_DISPLAY_IN_HOVER_TOOLTIP = {}

		// submanagers
		this._mapboxStyleManager = null
		this._storyDataManager = null
		this._statsManager = null
		
		this._storymap = sampleStoryMap
	}

	initWithContent = ({storymap, basicMapStyle, detailedMapStyle, satelliteMapStyle}) => {
		
		this._basicMapStyle = basicMapStyle
		this._detailedMapStyle = detailedMapStyle
		this._satelliteMapStyle = satelliteMapStyle

		this._generateConstants()
		this._generateManagers()
		this._generateCartoDataAndMetadata()

		this._isReady = true
	}

	// helper to redirect to stats
	get stats() { return this._statsManager }

	_generateConstants = () => {
		// api config
		this.READ_ONLY_CARTO_API_KEY = this._storymap.READ_ONLY_CARTO_API_KEY

		this.STATS_KEYWORDS = listToLookupTable(this._storymap.STATS_KEYWORDS)
		this.DASHBOARD_COMPONENT_TYPES = listToLookupTable(this._storymap.DASHBOARD_COMPONENT_TYPES)
		this._ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS = listToLookupTable(this._storymap.ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS)
		this._ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS = listToLookupTable(this._storymap.ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS)
		
		this.MAP_MENU_OPT = this._storymap.MAP_MENU_OPT
		this.MAP_MENU_BUTTONS_TO_DISPLAY = this._storymap.MAP_MENU_BUTTONS_TO_DISPLAY

		this.ZOOM_LVL_SWAP_BASEMAPS = this._storymap.ZOOM_LVL_SWAP_BASEMAPS

		// layer data lookup tables
		this.ICONS_TO_LOAD = listOfAllPossibleLayers.ICONS_TO_LOAD
		this.LAYER_TYPES = listOfAllPossibleLayers.LAYER_TYPES
		this.LIST_OF_ALL_POSSIBLE_LAYERS = listOfAllPossibleLayers.LIST_OF_ALL_POSSIBLE_LAYERS

		this.LIST_OF_ALL_POSSIBLE_LAYERS.filter(layerInfo => this._storymap.layers.includes(layerInfo.nameInCode))
		  .forEach(layerInfo => {
			const { cartoId, category, nameInCode } = layerInfo

			this.LAYER_NAMES_GROUPED_BY_CATEGORY[category] = [
			  ...(this.LAYER_NAMES_GROUPED_BY_CATEGORY[category] || []),
			  cartoId
			]

			this.LAYER_NAMES_TO_ID[nameInCode] = cartoId
		})

		this.LAYER_NAMES = Object.values(this.LAYER_NAMES_TO_ID)
	}

	// NOTE: Managers need to be initialized in specific order due to dependencies in functionality/data
	_generateManagers = () => {
		this._mapboxStyleManager = new MapboxStyleManager({
			LAYER_TYPES: this.LAYER_TYPES,
			LAYER_NAMES: this.LAYER_NAMES,
			layerNameToIdCallback: this.layerNameToId,
			basicMapStyle: this._basicMapStyle,
			detailedMapStyle: this._detailedMapStyle,
			satelliteMapStyle: this._satelliteMapStyle
		})

		this._storyDataManager = new StoryDataManager({
			storymap: this._storymap,
			createLayerPaintPropertyCallback: this._createLayerPaintProperty,
			layerNameToIdCallback: this.layerNameToId
		})

		this._statsManager = new StatsManager({
			apiKey: this.READ_ONLY_CARTO_API_KEY,
			sqlFilterToApplyToAllACSLayers: this.sqlFilterToApplyToAllACSLayers(),
			ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS: this._ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS,
			ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS: this._ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS,
		})
	}

	// NOTE: Populate CARTO_DATA *after* we instantiate other basic info/functions needed
	_generateCartoDataAndMetadata = () => {
		// populate carto data
		const acsLayersMetadata = {}
		this.LIST_OF_ALL_POSSIBLE_LAYERS.filter(layerInfo => this._storymap.layers.includes(layerInfo.nameInCode))
		  .forEach(layerInfo => {
		  	if (isAttrLayer(layerInfo.cartoId)) {
		  		const GIS_SPECIFIC_FIELDS = ['the_geom','the_geom_webmercator', 'lat', 'lon', 'namelsad', 'display_name', 'geoid', ...getLayerSpecificFields(layerInfo.cartoId)]
					const ACS_ATTRIBUTE_FIELDS = [...Object.values(this._ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS), ...Object.values(this._ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS)]
					acsLayersMetadata[layerInfo.cartoId] = [...GIS_SPECIFIC_FIELDS, ...ACS_ATTRIBUTE_FIELDS]
		  		const fieldsUsedInTheApp = `${ACS_ATTRIBUTE_FIELDS.join(',')},${GIS_SPECIFIC_FIELDS.join(',')}`
					const sqlFilterToApplyToAllACSLayers = this.sqlFilterToApplyToAllACSLayers() && ` WHERE ${this.sqlFilterToApplyToAllACSLayers()}`
					layerInfo.sql = `${layerInfo.sql.replace(NECESSARY_FIELDS_PLACEHOLDER, fieldsUsedInTheApp)}${sqlFilterToApplyToAllACSLayers}`
				}
				this.CARTO_DATA[layerInfo.cartoId] = layerInfo
				if (!!this._storymap.LOCAL_ASSETS){
					if (this._storymap.LOCAL_ASSETS.includes(layerInfo.nameInCode)) this.LOCAL_ASSET_CARTO_DATA[layerInfo.cartoId] = layerInfo
					this._createCartoLayer(layerInfo.cartoId, layerInfo)
				}
		})
		// console.log(acsLayersMetadata, JSON.stringify(acsLayersMetadata)) // @Chris: uncomment this for a JSON of the ACS Layer meta data

		// setup, not actually using right now
		//this._statsManager.loadAllStats()

		// populate displayname data
		this.getCartoDataLayers().forEach(layerId => {
			this.DISPLAY[layerId] = this.CARTO_DATA[layerId].displayName
			this.LAYER_LABEL_FIELD_IF_ANY[layerId] = this.CARTO_DATA[layerId].label || null
			this.POLYGON_DATA_FILL_COLOR_IF_ANY[layerId] = this.CARTO_DATA[layerId].fillColor || null
			this.POINT_DATA_ICON_IF_ANY[layerId] = this.CARTO_DATA[layerId].iconImage || null
			this.FIELDS_TO_DISPLAY_IN_HOVER_TOOLTIP[layerId] = this.CARTO_DATA[layerId].fieldsToDisplayInHoverTooltip || []
		})

		// Metadata
		this._statsManager.loadMetadata()
	}

	// ------------------------------------------------
	// Mapbox + Carto layers, redirecting the Carto API call
	// ------------------------------------------------

	getMapboxStyleWithCartoLayers = (mapReference, onSuccess) => {
		return getMapboxStyleWithFreshCartoUrl(mapReference, onSuccess)
	}

	// ------------------------------------------------
	// Reading constants, layers, and carto data
	// ------------------------------------------------

	readOnlyCartoApiKey = () => {
		return this.READ_ONLY_CARTO_API_KEY
	}

	getDashboardComponentTypes = () => {
		return this.DASHBOARD_COMPONENT_TYPES
	}
	
	getStartScreenOptions = () => {
		return listToLookupTable(this._storymap.START_SCREEN_OPTIONS)
	}
	
	getStartScreenOption = () => {
		return this._storymap.startScreenOption
	}
	
	get ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS() {
		return this._ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS
	}
	
	get ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS() {
		return this._ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS
	}

	getCartoDataLayers = () => {
		return Object.keys(this.CARTO_DATA)
	}
	
	getCartoLocalAssetDataLayers = () => {
		return Object.keys(this.LOCAL_ASSET_CARTO_DATA)
	}

	getCartoDataForLayerId = (layerId) => {
		return layerId in this.CARTO_DATA ? this.CARTO_DATA[layerId] : null
	}

	layerNameToId = (layerName) => {
		return this.LAYER_NAMES_TO_ID[layerName]
	}

	getLayerDisplayName = (layerId) => {
		return this.DISPLAY[layerId]
	}
	
	getFieldsToDisplayInHoverTooltip = (layerId) => {
		return this.FIELDS_TO_DISPLAY_IN_HOVER_TOOLTIP[layerId]
	}

	getLayerLabelFieldIfAny = (layerId) => {
		return this.LAYER_LABEL_FIELD_IF_ANY[layerId]
	}
	
	getPointDataIconIfAny = (layerId) => {
		return this.POINT_DATA_ICON_IF_ANY[layerId]
	}
	
	getPolygonDataFillColorIfAny = (layerId) => {
		return this.POLYGON_DATA_FILL_COLOR_IF_ANY[layerId]
	}

	includesLayerId = (layerId) => {
		return Object.values(this.LAYER_NAMES_TO_ID).includes(layerId)
	}

	getLayerNames = () => {
		return this.LAYER_NAMES
	}

	getCategories = () => {
		return Object.keys(this.LAYER_NAMES_GROUPED_BY_CATEGORY)
	}

	getDefaultCategory = () => {
		// assume first is the default
		return Object.keys(this.LAYER_NAMES_GROUPED_BY_CATEGORY)[0]
	}

	getLayersInCategory = (categoryId) => {
		return this.LAYER_NAMES_GROUPED_BY_CATEGORY[categoryId]
	}
	
	getMapMenuOptions = () => {
		return this._storymap.MAP_MENU_OPT
	}
	
	getMapMenuButtonsToDisplay = () => {
		return this._storymap.MAP_MENU_BUTTONS_TO_DISPLAY
	}

	// ------------------------------------------------
	// MapboxStyleManager
	// ------------------------------------------------
	defaultMapStyle = () => {
		return this._mapboxStyleManager.defaultMapStyle()
	}

	detailedMapStyle = () => {
		return this._mapboxStyleManager.detailedMapStyle()
	}

	// wrapper to create a StyledDataLayersList object for storymaps
	_createLayerPaintProperty = (info, location) => {
		return this._mapboxStyleManager.createLayerPaintProperty(info, location)
	}

	// create carto layers when importing list-of-possible-layers.yaml
	_createCartoLayer = (cartoLayerId, cartoInfo) => {
		this._mapboxStyleManager.createCartoLayer(cartoLayerId, cartoInfo)
	}

	// get default style props for a carto style when fetching the style from Carto
	getStyleForCartoLayer = (cartoLayerId) => {
		return this._mapboxStyleManager.getStyleForCartoLayer(cartoLayerId)
	}

	getZoomLevelToSwapBasemaps = () => {
		return this.ZOOM_LVL_SWAP_BASEMAPS
	}

	swapBaseLayers = (map, oldBase, newBase) => {
        this._mapboxStyleManager.swapBaseLayers(map, oldBase, newBase)
    }

	// ------------------------------------------------
	// StoryDataManager
	// ------------------------------------------------

	getStoryScenes = () => {
		// PLEASE REMOVE THIS FUNCTION AFTER REMOVING OTHER REFERENCES
		// to no longer have storyDataManager.getScenes()
		return this._storyDataManager ? this._storyDataManager.getScenes() : []
	}

	getStorySceneAtIndex = (sceneIdx) => {
		return this._storyDataManager && this._storyDataManager.getSceneAtSceneIndex(sceneIdx)
	}

	getDashboardsAtIndex = (sceneIdx) => {
		return this._storyDataManager.getDashboardsAtSceneIndex(sceneIdx)
	}
	
	_getCommunitySummaryComponent = dashboard => {
		const DASHBOARD_COMPONENT_TYPES = this.getDashboardComponentTypes()
		return dashboard.components.find(component => component.type === DASHBOARD_COMPONENT_TYPES.COMMUNITY_SUMMARY_COMPONENT)
	}
	
	communitySummaryFieldsAtIndex = (sceneIdx) => {
		const communitySummaryDashboard = this.getDashboardsAtIndex(sceneIdx)
			.find(this._getCommunitySummaryComponent)
		return communitySummaryDashboard ? this._getCommunitySummaryComponent(communitySummaryDashboard).fields : []
	}
	
	communitySummaryFieldNamesAtIndex = (sceneIndx) => this.communitySummaryFieldsAtIndex(sceneIndx).map(field => field.fieldName)

	// helper for when we expect only one dashboard
	defaultDashboardAtIndex = (sceneIdx) => {
		return this._storyDataManager.getDashboardsAtSceneIndex(sceneIdx)[0]
	}

	updateLocationForStoryScenes = (locationInfo) => {
		this._storyDataManager.updateLocationForStoryScenes(locationInfo)
	}

	getCustomIconsToLoad = () => {
		return this.ICONS_TO_LOAD
	}

	hasPredefinedStartingLocation = () => {
		return this._storyDataManager.hasStartingLocation() && this.shouldSkipIntro()
	}

	getPredefinedStartingLocation = () => {
		return this._storyDataManager.getStartingLocation()
	}

	shouldSkipIntro = () => {
		return this._storyDataManager.shouldSkipIntroScreen()
	}
	
	clickingACommunityOnTheMapShouldCompareIt = () => {
		return this._storyDataManager.clickingACommunityOnTheMapShouldCompareIt()
	}
	
	mapTitle = () => {
		return this._storyDataManager.mapTitle()
	}
	
	sqlFilterToApplyToAllACSLayers = () => {
		return this._storyDataManager.sqlFilterToApplyToAllACSLayers()
	}

	getDashboardHeader = () => {
		return this._storyDataManager.getDashboardHeader()
	}
	
	getVizColorThemeForCurrentSlide = (currentSceneIndex) => {
		return FIVE_CHLOROPLETH_COLORS
		// const DASHBOARD_COMPONENT_TYPES = this.getDashboardComponentTypes()
		// const DEFAULT_VIZ_COLOR_THEME = "theme(MULTI_HUE_CYAN_BLUE)"
		//
		// const leftPanel = this.getDashboardsAtIndex(currentSceneIndex).find(dashboard => dashboard.type === "LEFT_PANEL")
		// const singleVizComponent = (leftPanel && leftPanel.components.find(component => component.type === DASHBOARD_COMPONENT_TYPES.SINGLE_INDICATOR_VIS)) || {}
		// const colorTheme = singleVizComponent.colorTheme || DEFAULT_VIZ_COLOR_THEME
		// const arrayOfColors = getColorsHSLAFromTheme(colorTheme, 5)
		
		// return arrayOfColors
	}

	getFilterFieldOptionsForCurrentSlide = (currentSceneIndex) => {
		const DASHBOARD_COMPONENT_TYPES = this.getDashboardComponentTypes()

		const leftPanel = this.getDashboardsAtIndex(currentSceneIndex).find(dashboard => dashboard.type === "LEFT_PANEL")
		const singleVizComponent = (leftPanel && leftPanel.components.find(component => component.type === DASHBOARD_COMPONENT_TYPES.SINGLE_INDICATOR_VIS)) || {}
		return singleVizComponent.dataOptions || []
	}
}

// export access to a singleton instance
export default new DataManager()
